// Admin > Audit Log — read-only history of Create/Update/Delete operations
// captured by BaseService.createOrUpdateByModel/Tran (see AuditLogService.ts
// on the backend). Covers the majority of write paths that go through that
// shared method; a few services that write via direct model calls
// (StockTrackService, ProductItemService, BillingItemsService, OtpService,
// orderService, bannerService, StockHistoryLogService) aren't captured here.
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import { Input } from "ui/input";
import moment from "moment";
import { DateRange } from "react-day-picker";
import { ColumnDef } from "@tanstack/react-table";
import { DataTable } from "../shared/data-table";
import { StatusBadge } from "../shared/status-badge";
import { Button } from "ui/button";
import { Combobox } from "ui/combobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "ui/sheet";
import { DateRangePicker } from "../Components/Reports/DateRangePicker";
import ApparelMenu from "../Components/SubComponents/apparelMenu";
import { getAuditLogListAction } from "../Store/actions/apparelAction";

// Raw Sequelize model name (what's actually stored in tbl_auditlog.moduleName,
// via BaseService's `model.name`) -> the label this app's own nav/menus use
// for that entity, so the log reads the same way the rest of the UI does.
const MODULE_NAME_LABELS: Record<string, string> = {
    productDetails: "Stock",
    productHistory: "Stock Ledger",
    stockHistoryLog: "Stock Ledger",
    stockTrack: "Stock",
    productItem: "Product Item",
    tbl_procurement: "Procurement",
    billing: "Customer Bills",
    billingItems: "Customer Bills",
    tbl_vendorpurchase: "Vendor Bills",
    rental: "Rental",
    tbl_vendor: "Vendor",
    tbl_vendordebtledger: "Vendor",
    customerDetail: "Customers",
    tbl_employee: "Employee",
    category: "Category",
    subCategory: "Sub Category",
    categoryAndSubcategoryMapping: "Category",
    cycleCountJob: "Cycle Count",
    cycleCountJobDetails: "Cycle Count",
    tbl_cycleCountMultiProductTrack: "Cycle Count",
    tbl_salesreturn: "Sales Return",
    tbl_banner: "Banners",
    tbl_order: "Online Billing",
    tbl_orderItems: "Online Billing",
    tbl_orderItemsTrack: "Online Billing",
    tbl_attachment: "Attachment",
    user: "User",
    tbl_attendance: "Attendance",
    tbl_customercreditledger: "Customer Credit Ledger",
};

function moduleLabel(rawModuleName: string | undefined | null): string {
    if (!rawModuleName) return "-";
    return MODULE_NAME_LABELS[rawModuleName] || rawModuleName;
}

// Reverse of MODULE_NAME_LABELS: friendly label -> every raw module name that
// maps to it (e.g. "Customer Bills" -> ["billing", "billingItems"]), since
// the filter is driven by the label but the backend stores raw names.
const MODULE_LABEL_TO_RAW_NAMES: Record<string, string[]> = Object.entries(MODULE_NAME_LABELS).reduce(
    (acc, [raw, label]) => {
        acc[label] = acc[label] || [];
        acc[label].push(raw);
        return acc;
    },
    {} as Record<string, string[]>
);

// Only modules actually reachable from the sidebar (see src/shared/navigation.ts)
// are offered as filters — several raw model names are auditable but have no
// corresponding nav item (Product Item, Sub Category, Cycle Count, Banners,
// Online Billing, Attachment, User), so they're excluded here.
const SIDEBAR_MODULE_LABELS = [
    "Stock", "Stock Ledger", "Procurement", "Sales Return", "Customer Bills", "Vendor Bills",
    "Customers", "Vendor", "Category", "Employee", "Attendance",
];

const MODULE_FILTER_OPTIONS = SIDEBAR_MODULE_LABELS
    .filter((label) => MODULE_LABEL_TO_RAW_NAMES[label])
    .sort()
    .map((label) => ({ value: label, label }));

// recordId itself is always the real UUID internally — never shown. The
// Record Name/ID column shows the module's own business-facing identifier
// instead (see BaseService.pickRecordName on the backend: Product ID for
// Stock, Invoice ID for Customer/Vendor Bills, the natural name for
// everything else) — no cosmetic "RECID-N" prefix.
function recordLabel(recordName?: string): string {
    return recordName || "-";
}

// recordName is always "<business code> / <descriptive text>" (see
// BaseService.pickRecordName on the backend) — the business code is what
// each module's own search box matches on, so we hand it to the target
// screen to re-run the same search a user would type by hand.
function recordCode(recordName?: string | null): string | undefined {
    if (!recordName) return undefined;
    return recordName.split(" / ")[0]?.trim() || undefined;
}

// Friendly module label -> the route its own screen lives at, so clicking a
// Record Name/ID always lands on the record's actual module — driven by
// moduleName, never guessed from the record's own ID text (e.g. an
// Attendance record's id looks like "EMP-0004", but it must still open
// Attendance, not Employee). Category and Sub Category share the same
// screen (category.tsx resolves which one the recordId belongs to).
// Modules with no standalone screen of their own (Product Item, Cycle
// Count, Banners, Online Billing, Attachment, User) are left unmapped, so
// those cells just stay plain text instead of linking nowhere useful.
const MODULE_ROUTES: Record<string, string> = {
    "Stock": "/inventoryManagement",
    "Stock Ledger": "/stock/history",
    "Procurement": "/procurement",
    "Sales Return": "/salesReturn",
    "Customer Bills": "/billing",
    "Vendor Bills": "/vendor-purchase",
    "Rental": "/rental",
    "Customers": "/customer",
    "Vendor": "/vendor",
    "Category": "/mugizh-admin/category",
    "Sub Category": "/mugizh-admin/category",
    "Employee": "/mugizh-admin/employee",
    "Attendance": "/attendance",
};

const CLOUD_FRONT_BASE = `${process.env.REACT_APP_AWS_CLOUD_FRONT_URL}/${process.env.REACT_APP_IMAGE_DESTINATION_BUCKET}`;

// Diffed field name -> the S3/CloudFront folder its stored fileUuid value
// lives under (matches checkType() in awsUpload.ts). Extend this as other
// modules grow their own single-image fields.
const IMAGE_DIFF_FIELDS: Record<string, string> = {
    imageFileUuid: "procurement/images/",
};

// Raw diffed column name -> a human-friendly label for the Field column.
// Anything not listed here just falls back to showing the raw name as-is.
const FIELD_LABELS: Record<string, string> = {
    imageFileUuid: "Image Upload",
    itemCount: "Stock Quantity",
    invoiceId: "Invoice ID",
    purchaseDate: "Purchase Date",
    billedByEmployeeId: "Billed By",
    totalamount: "Total Amount",
    roundOff: "Round Off",
    discount: "Discount",
    amount: "Amount",
    stateRefId: "State",
    categoryId: "Category",
    subCategoryId: "Sub Category",
    coreCategoryId: "Core Category",
    categoryAndSubCategoryMappingId: "Category / Sub Category",
    vendorId: "Vendor",
    workTypeRefId: "Work Type",
    salesType: "Sales Type",
    sizeRefId: "Size",
    colorRefId: "Color",
    status: "Status",
    statusRefId: "Status",
    billingId: "Invoice",
    productItemId: "Product",
    mrp: "MRP",
    returnedAmount: "Returned Amount",
    quantity: "Quantity",
    whatsappGroupStatus: "WhatsApp Group Joined",
    isNotWhatsappNumber: "Not a WhatsApp Number",
    // Customer Credit Ledger. The ids behind these are resolved to their
    // display values server-side (see AuditLogService's FIELD_RESOLVERS), so
    // these labels sit over a name, not a raw ref id.
    customerDetailId: "Customer",
    transactionTypeRefId: "Transaction Type",
    direction: "Direction",
    paymentMethodRefId: "Payment Method",
    billTypeRefId: "Bill Type",
    previousBalance: "Previous Balance",
    newBalance: "New Balance",
    transactionDate: "Transaction Date",
    creditLimit: "Credit Limit",
    creditBalance: "Credit Balance",
    remarks: "Remarks",
};

function fieldLabel(field: string): string {
    return FIELD_LABELS[field] || field;
}

// Fields with no value worth reviewing in the Audit Log — hidden everywhere,
// regardless of module, on top of any per-module allowlist below.
const GLOBAL_HIDDEN_FIELDS = new Set(["isMultipleRecords", "weight", "onlineDiscount"]);

// Per-module allowlist (and display order) for the Audit Log Detail view —
// a module's full diff can include internal/FK columns (gst, orderId,
// paymentMethodRefId, customerDetailId, billTypeRefId, returnamount,
// isOnlineOrder for billing) that are noise for a business user reviewing
// what changed. Modules not listed here fall back to showing every changed
// field (minus GLOBAL_HIDDEN_FIELDS), in whatever order the diff happened
// to store them.
const MODULE_FIELD_ALLOWLIST: Record<string, string[]> = {
    billing: ["invoiceId", "purchaseDate", "billedByEmployeeId", "totalamount", "roundOff", "discount", "amount"],
};

// Fields that are conceptually a calendar date, not a point in time — shown
// without a time-of-day even though the column is stored as a full DATETIME.
const DATE_ONLY_FIELDS = new Set(["purchaseDate"]);

const OPERATION_OPTIONS = [
    { value: "", label: "All Operations" },
    { value: "Create", label: "Create" },
    { value: "Update", label: "Update" },
    { value: "Delete", label: "Delete" },
];

const OPERATION_VARIANT: Record<string, any> = {
    Create: "success",
    Update: "warning",
    Delete: "destructive",
};

// Diffed field values are raw column values from the DB, so date/datetime
// fields (e.g. purchaseDate) come through as ISO strings — reformat those to
// match the same "DD-MMM-YYYY, hh:mm A" style used for Date & Time above.
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

// Credit-ledger `direction` is a raw DB enum ("DEBIT"/"CREDIT") — shown in
// the same title case the rest of the log uses.
const DIRECTION_LABELS: Record<string, string> = { DEBIT: "Debit", CREDIT: "Credit" };

function formatDiffValue(value: any, field?: string): string {
    if (value == null || value === "") return "-";
    if (field === "isActive") return Number(value) === 1 ? "Active" : "Inactive";
    if (field === "direction") return DIRECTION_LABELS[String(value).toUpperCase()] || String(value);
    if (typeof value === "string" && ISO_DATE_PATTERN.test(value)) {
        return moment(value).format(field && DATE_ONLY_FIELDS.has(field) ? "DD-MMM-YYYY" : "DD-MMM-YYYY, hh:mm A");
    }
    return String(value);
}

// Image-upload fields (see IMAGE_DIFF_FIELDS) render as an actual thumbnail
// instead of the raw stored fileUuid, so "previous image vs new image" is a
// visual comparison rather than two unreadable filenames.
function renderDiffCell(field: string, value: any) {
    const folder = IMAGE_DIFF_FIELDS[field];
    if (!folder) return formatDiffValue(value, field);
    if (value == null || value === "") return <span className="text-muted-foreground">No image</span>;
    return (
        <img
            src={`${CLOUD_FRONT_BASE}${folder}${value}`}
            alt={field}
            className="h-12 w-12 rounded-lg border object-cover"
        />
    );
}

// previousValue/newValue are stored server-side as JSON-stringified
// {field: value} diffs (only the fields that actually changed).
function parseDiff(value: any): Record<string, any> {
    if (!value) return {};
    try {
        return typeof value === "string" ? JSON.parse(value) : value;
    } catch {
        return {};
    }
}

const AuditLog = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
    const { getAuditLogListOnLoad, getAuditLogListData } = apparelReducerState;

    const auditLogList = getAuditLogListData?.getAuditLogList?.auditLogList;
    const auditLogPage = getAuditLogListData?.getAuditLogList?.page;

    const [searchTemp, setSearchTemp] = React.useState({
        searchText: "",
        moduleLabel: "",
        operation: "",
        fromDate: "",
        toDate: "",
        offset: 0,
        page: 1,
    });
    const [dateRange, setDateRange] = React.useState<DateRange | undefined>(undefined);
    const [viewEntry, setViewEntry] = React.useState<any>(null);

    const getAuditLogListApi = () => {
        dispatch(getAuditLogListAction({
            searchText: searchTemp?.searchText,
            moduleNames: searchTemp?.moduleLabel ? MODULE_LABEL_TO_RAW_NAMES[searchTemp.moduleLabel] : undefined,
            operation: searchTemp?.operation,
            fromDate: searchTemp?.fromDate,
            toDate: searchTemp?.toDate,
            paging: { limit: 10, offset: searchTemp?.offset },
        }));
    };

    React.useEffect(() => {
        getAuditLogListApi();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchTemp]);

    // Own debounced search (rather than DataTable's built-in `searchable` slot)
    // so this toolbar's element order — search, then module filter — is fully
    // under this page's control instead of the shared component's layout.
    const [searchInput, setSearchInput] = React.useState("");
    React.useEffect(() => {
        const t = setTimeout(() => {
            setSearchTemp((s) => (s.searchText === searchInput ? s : { ...s, searchText: searchInput, offset: 0 }));
        }, 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchInput]);

    const onModuleLabelChange = (moduleLabel: string) => {
        setSearchTemp((s) => ({ ...s, moduleLabel, offset: 0 }));
    };

    const onOperationChange = (operation: string) => {
        setSearchTemp((s) => ({ ...s, operation, offset: 0 }));
    };

    const onDateRangeChange = (range: DateRange | undefined) => {
        setDateRange(range);
        setSearchTemp((s) => ({
            ...s,
            fromDate: range?.from ? moment(range.from).format("YYYY-MM-DD") : "",
            toDate: range?.to ? moment(range.to).format("YYYY-MM-DD") : "",
            offset: 0,
        }));
    };

    const onPageChange = (nextPage: number) => {
        const offset = ((nextPage * 10) - 10 === 0) ? 0 : (nextPage * 10) - 10;
        setSearchTemp((s) => ({ ...s, page: nextPage, offset }));
    };

    // Two shapes of credit-related audit entries, both routed to the Credit
    // Ledger screen instead of the Customers list/edit sheet:
    //   - customerDetail rows whose diff touched creditLimit (the only
    //     credit field that's actually audited on that module — see
    //     CustomerCreditService.ts, which passes skipAudit:true for
    //     creditBalance/openingBalanceRecorded writes since the ledger
    //     itself is their audit trail).
    //   - tbl_customercreditledger rows themselves (every Opening Balance /
    //     Usage / Settlement transaction) — labeled "Customers" in
    //     MODULE_NAME_LABELS above so they share that module filter. Their
    //     recordId packs both ids the deep link needs,
    //     "<customerKey>|<ledgerKey>" (see BaseService.recordAudit), so the
    //     route param and the highlighted row come from the same entry.
    // tbl_vendordebtledger rows are labeled "Vendor" so they share that
    // module filter, but they must open the Vendor Debt ledger rather than the
    // Vendor list. Their recordId packs both ids the deep link needs —
    // "<vendorKey>|<ledgerKey>" (see BaseService.recordAudit). Older rows, or
    // a row written before the owner could be resolved, may carry only the
    // owner key, so the ledger key is optional here.
    const isVendorDebtEntry = (entry: any) => entry?.moduleName === "tbl_vendordebtledger";

    // Shared by both ledger modules (vendor debt and customer credit), which
    // pack their recordId identically: "<ownerKey>|<ledgerKey>".
    const parseLedgerRecordId = (recordId?: string | null) => {
        if (!recordId) return {};
        const raw = String(recordId);
        if (!raw.includes("|")) return { ownerId: raw || undefined };
        const [ownerId, ledgerId] = raw.split("|");
        return { ownerId: ownerId || undefined, ledgerId: ledgerId || undefined };
    };

    // Rows written before the compound-recordId change stored only the ledger
    // row's own uniqueKey, which is not enough to build the /vendor-debt route
    // — those stay plain text rather than linking to a dead screen.
    const isVendorDebtLinkable = (entry: any) =>
        isVendorDebtEntry(entry) && !!parseLedgerRecordId(entry?.recordId).ledgerId;

    const isCreditRelated = (entry: any) => {
        if (entry?.moduleName === "tbl_customercreditledger") return true;
        if (entry?.moduleName !== "customerDetail") return false;
        const changed = new Set([...Object.keys(parseDiff(entry?.previousValue)), ...Object.keys(parseDiff(entry?.newValue))]);
        return changed.has("creditLimit");
    };

    // Deep-links into the record's own module instead of just its list —
    // each target screen re-runs its own existing search with
    // auditSearchCode, matches the row whose own uniqueKey-aliased field
    // equals auditRecordId, and opens it exactly as a manual click would
    // (Customers opens straight into the Edit sheet, same as Employee).
    const navigateToRecord = (entry: any) => {
        const recordId = entry?.recordId;
        const recordName = entry?.recordName;
        // Checked before the generic MODULE_ROUTES lookup, which would
        // otherwise send these to the Vendor list on the "Vendor" label.
        if (isVendorDebtEntry(entry)) {
            const { ownerId: vendorId, ledgerId: debtId } = parseLedgerRecordId(recordId);
            if (!vendorId || !debtId) return;
            navigate(`/vendor-debt/${vendorId}`, { state: { highlightDebtId: debtId } });
            return;
        }
        if (isCreditRelated(entry)) {
            // Credit Ledger is a plain list with no edit sheet of its own —
            // the only way to show "this is the record that changed" is to
            // highlight something. A tbl_customercreditledger entry IS a real
            // transaction, and its own uniqueKey rides along in recordId, so
            // the screen highlights that exact row (it used to highlight
            // "the newest row", which flagged the wrong transaction whenever
            // an older audit entry was clicked). Entries written before the
            // compound recordId carry no ledger key — those open the ledger
            // with nothing highlighted rather than pointing at a row that
            // isn't the audited one.
            // A customerDetail creditLimit change is NOT a ledger transaction
            // at all — there's no row that corresponds to it, so it
            // highlights the Credit Limit summary card instead.
            const isLedgerEntry = entry?.moduleName === "tbl_customercreditledger";
            const { ownerId: customerId, ledgerId } = parseLedgerRecordId(recordId);
            if (!customerId) return;
            navigate(`/credit-ledger/${customerId}`, {
                state: isLedgerEntry ? { highlightCreditLedgerId: ledgerId } : { highlightCreditLimitCard: true }
            });
            return;
        }
        const route = MODULE_ROUTES[moduleLabel(entry?.moduleName)];
        if (!route) return;
        navigate(route, { state: { auditRecordId: recordId, auditSearchCode: recordCode(recordName) } });
    };

    const columns = React.useMemo<ColumnDef<any, any>[]>(
        () => [
            { id: "moduleName", header: "Module Name", cell: ({ row }) => moduleLabel(row.original?.moduleName), meta: { sticky: "left" } },
            {
                id: "recordName",
                header: "Record Name/ID",
                cell: ({ row }) => {
                    const label = recordLabel(row.original?.recordName);
                    const linkable = isVendorDebtLinkable(row.original) || isCreditRelated(row.original) || !!MODULE_ROUTES[moduleLabel(row.original?.moduleName)];
                    if (!linkable) return label;
                    return (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                navigateToRecord(row.original);
                            }}
                            className="text-left text-primary hover:underline"
                        >
                            {label}
                        </button>
                    );
                },
            },
            {
                id: "operation",
                header: "Operation",
                cell: ({ row }) => <StatusBadge status={row.original?.operation} variant={OPERATION_VARIANT[row.original?.operation]} />,
            },
            { id: "performedBy", header: "Performed By", cell: ({ row }) => row.original?.performedByName || "-" },
            {
                id: "performedOn",
                header: "Date & Time",
                cell: ({ row }) => (row.original?.performedOn ? moment(row.original?.performedOn).format("DD-MMM-YYYY, hh:mm A") : "-"),
            },
            {
                id: "action",
                header: "Action",
                cell: ({ row }) => (
                    <Button variant="link" className="h-auto p-0" onClick={() => setViewEntry(row.original)}>
                        View
                    </Button>
                ),
                meta: { sticky: "right" },
            },
        ],
        []
    );

    const filterToolbar = (
        <div className="flex flex-nowrap items-center gap-1.5">
            <div className="relative w-44">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    placeholder="Search record name/ID"
                    className="h-9 pl-8 text-sm"
                    aria-label="Search by record name or record ID"
                />
            </div>
            <Combobox
                options={MODULE_FILTER_OPTIONS}
                value={searchTemp?.moduleLabel || undefined}
                onValueChange={onModuleLabelChange}
                placeholder="Module"
                searchPlaceholder="Search module..."
                className="h-9 w-32"
            />
            {searchTemp?.moduleLabel && (
                <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9"
                    aria-label="Clear module filter"
                    onClick={() => onModuleLabelChange("")}
                >
                    <X className="h-4 w-4" />
                </Button>
            )}
            <Select value={searchTemp?.operation || "all"} onValueChange={(v) => onOperationChange(v === "all" ? "" : v)}>
                <SelectTrigger className="h-9 w-32"><SelectValue /></SelectTrigger>
                <SelectContent>
                    <SelectItem value="all">All Operations</SelectItem>
                    {OPERATION_OPTIONS.filter((o) => o.value).map((o) => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <DateRangePicker value={dateRange} onChange={onDateRangeChange} disableFuture placeholder="Date" className="h-9 w-36" />
        </div>
    );

    const previousDiff = parseDiff(viewEntry?.previousValue);
    const newDiff = parseDiff(viewEntry?.newValue);
    const rawChangedFields = Array.from(new Set([...Object.keys(previousDiff), ...Object.keys(newDiff)]));
    const allowlist = MODULE_FIELD_ALLOWLIST[viewEntry?.moduleName];
    const changedFields = (allowlist ? allowlist.filter((f) => rawChangedFields.includes(f)) : rawChangedFields)
        .filter((f) => !GLOBAL_HIDDEN_FIELDS.has(f));

    return (
        <div className="mz h-full overflow-y-auto px-6 py-4">
            <ApparelMenu selectedKey="audit-log" />
            <DataTable
                columns={columns}
                data={auditLogList || []}
                loading={getAuditLogListOnLoad}
                hideDensityToggle
                toolbar={filterToolbar}
                title="Audit Log"
                pagination={{
                    page: auditLogPage?.currentPage || 1,
                    total: auditLogPage?.totalCount || 0,
                    onChange: onPageChange,
                }}
            />

            <Sheet open={!!viewEntry} onOpenChange={(o) => { if (!o) setViewEntry(null); }}>
                <SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-lg">
                    <SheetHeader className="border-b px-5 py-4">
                        <SheetTitle>Audit Log Detail</SheetTitle>
                    </SheetHeader>
                    <div className="flex-1 overflow-y-auto p-5">
                        <div className="mb-4 space-y-1 text-sm">
                            <div><span className="text-muted-foreground">Module Name:</span> {moduleLabel(viewEntry?.moduleName)}</div>
                            <div>
                                <span className="text-muted-foreground">Record Name/ID:</span>{" "}
                                {(() => {
                                    const label = recordLabel(viewEntry?.recordName);
                                    const linkable = isVendorDebtLinkable(viewEntry) || isCreditRelated(viewEntry) || !!MODULE_ROUTES[moduleLabel(viewEntry?.moduleName)];
                                    if (!linkable) return label;
                                    return (
                                        <button
                                            type="button"
                                            onClick={() => navigateToRecord(viewEntry)}
                                            className="text-primary hover:underline"
                                        >
                                            {label}
                                        </button>
                                    );
                                })()}
                            </div>
                            <div><span className="text-muted-foreground">Operation:</span> {viewEntry?.operation}</div>
                            <div><span className="text-muted-foreground">Performed By:</span> {viewEntry?.performedByName || "-"}</div>
                            <div>
                                <span className="text-muted-foreground">Date & Time:</span>{" "}
                                {viewEntry?.performedOn ? moment(viewEntry?.performedOn).format("DD-MMM-YYYY, hh:mm A") : "-"}
                            </div>
                        </div>

                        {changedFields.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No field-level changes recorded for this entry.</p>
                        ) : (
                            <table className="w-full border-collapse text-sm">
                                <thead>
                                    <tr className="border-b text-left text-muted-foreground">
                                        <th className="py-2 pr-2">Field</th>
                                        <th className="py-2 pr-2">Previous Value</th>
                                        <th className="py-2">New Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {changedFields.map((field) => (
                                        <tr key={field} className="border-b">
                                            <td className="py-2 pr-2 font-medium text-foreground">{fieldLabel(field)}</td>
                                            <td className="py-2 pr-2 text-muted-foreground">{renderDiffCell(field, previousDiff[field])}</td>
                                            <td className="py-2 text-foreground">{renderDiffCell(field, newDiff[field])}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
};

export default AuditLog;
